import type { ConversationTurnEntry } from '@/index'
import { AgentMode, EventType } from '@/index'
import type { FormattedAgentActivity } from '@/features/events/activity/utils/types'
import { buildMockEvent, buildToolNotificationContext } from './mockSessions'

export interface DemoActivityStep {
  readonly activity: FormattedAgentActivity | null
  readonly durationMs: number
  readonly eventOverride?: ConversationTurnEntry
}

export const DEMO_SPEED_MULTIPLIER = 1

// ---------------------------------------------------------------------------
// Pre-built event objects for each animation phase
// ---------------------------------------------------------------------------

const NARRATIVE_EVENT = buildMockEvent({
  eventType: EventType.AGENT_NARRATIVE,
  agentMode: AgentMode.DEFAULT_MODE,
  agentResponse:
    "I'll implement the OAuth2 callback handler and update the AuthProvider with PKCE flow support",
})

const FILE_MOD_EVENT = buildMockEvent({
  eventType: EventType.FILE_MODIFICATION,
  agentMode: AgentMode.DEFAULT_MODE,
  notificationMessage: 'File modified: OAuthCallback.tsx',
  toolName: 'Write',
  notificationContext: buildToolNotificationContext('Write'),
})

const TESTING_NARRATIVE_EVENT = buildMockEvent({
  eventType: EventType.AGENT_NARRATIVE,
  agentMode: AgentMode.DEFAULT_MODE,
  agentResponse: 'Running the auth test suite to verify the OAuth2 callback and token refresh flow',
})

const REPLY_EVENT = buildMockEvent({
  eventType: EventType.AGENT_REPLY,
  agentMode: AgentMode.DEFAULT_MODE,
  agentResponse:
    "I've completed the OAuth2 implementation. The PKCE flow is working with automatic token refresh and secure callback handling",
})

// ---------------------------------------------------------------------------
// Step builder
// ---------------------------------------------------------------------------

function buildStep(
  toolName: string,
  status: 'pre_tool' | 'post_tool',
  activityContext: string,
  durationMs: number,
  suffix?: string,
  eventOverride?: ConversationTurnEntry,
): DemoActivityStep {
  const activity: FormattedAgentActivity = {
    toolName,
    activityContext,
    status,
    agentMode: AgentMode.DEFAULT_MODE,
    ...(suffix === undefined ? {} : { suffix }),
  }

  return {
    activity,
    durationMs,
    ...(eventOverride === undefined ? {} : { eventOverride }),
  }
}

// ---------------------------------------------------------------------------
// Animation script — tells a complete OAuth2 implementation story
// ---------------------------------------------------------------------------

export const DEMO_ACTIVITY_SCRIPT: readonly DemoActivityStep[] = [
  // Phase 1: Discovery (AGENT_NARRATIVE event) — ~3.0s
  buildStep('Read', 'pre_tool', 'AuthProvider.tsx', 1200, undefined, NARRATIVE_EVENT),
  buildStep('Read', 'post_tool', 'AuthProvider.tsx', 400),
  buildStep('Grep', 'pre_tool', 'useAuth.*export', 1100),
  buildStep('Grep', 'post_tool', 'useAuth.*export', 300),

  // Phase 2: Implementation (FILE_MODIFICATION event) — ~3.5s
  buildStep('Edit', 'pre_tool', 'AuthProvider.tsx', 1500, '+12 -4', FILE_MOD_EVENT),
  buildStep('Edit', 'post_tool', 'AuthProvider.tsx', 300, '+12 -4'),
  buildStep('Write', 'pre_tool', 'OAuthCallback.tsx', 1400, '+47'),
  buildStep('Write', 'post_tool', 'OAuthCallback.tsx', 300, '+47'),

  // Phase 3: Testing (AGENT_NARRATIVE event — "Running tests...") — ~3.0s
  buildStep('Glob', 'pre_tool', 'src/**/*.test.tsx', 800, undefined, TESTING_NARRATIVE_EVENT),
  buildStep('Glob', 'post_tool', 'src/**/*.test.tsx', 300),
  buildStep('Bash', 'pre_tool', 'npm test -- auth', 1600),
  buildStep('Bash', 'post_tool', 'npm test -- auth', 300),

  // Phase 4: Completion (AGENT_REPLY event) — ~2.0s
  { activity: null, durationMs: 2000, eventOverride: REPLY_EVENT },

  // Phase 5: Waiting for input (cyan indicator) — ~3.0s
  {
    activity: {
      toolName: '',
      activityContext: 'Claude is waiting for your input',
      status: 'system' as const,
      agentMode: AgentMode.DEFAULT_MODE,
    },
    durationMs: 3000,
    eventOverride: REPLY_EVENT,
  },
]
